import { createClient } from '@supabase/supabase-js';

var supabaseUrl = 'https://crlpjrgbyvwdhzpcjokr.supabase.co';
var supabaseKey = 'sb_publishable_XNsX0pXrdfkpMglKwbKZmA_e-CNixek';

export var supabase = createClient(supabaseUrl, supabaseKey);

// ── Encryption ──

// Convert a Uint8Array to a base64 string. Doing this in chunks is essential —
// the naive `String.fromCharCode.apply(null, bytes)` form passes every byte as
// a separate function argument, which exceeds JS engine argument limits for
// blobs over ~65K bytes. On Safari/iOS this silently produces malformed
// output (rather than throwing), which writes to the DB successfully and then
// fails on the next decrypt with "Unexpected EOF". This was the root cause of
// repeated `bills` and `cards` data losses in earlier versions.
function bytesToBase64(bytes) {
  var chunkSize = 0x8000; // 32 KB per chunk — well under any engine's arg limit
  var binary = "";
  for (var i = 0; i < bytes.length; i += chunkSize) {
    var chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, chunk);
  }
  return btoa(binary);
}

async function deriveKey(password) {
  var enc = new TextEncoder();
  var keyMaterial = await window.crypto.subtle.importKey(
    "raw", enc.encode(password), "PBKDF2", false, ["deriveKey"]
  );
  return window.crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: enc.encode("vault-budget-salt"), iterations: 100000, hash: "SHA-256" },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

export async function encryptData(data, password) {
  if (data === undefined || data === null) {
    console.error("encryptData called with " + (data === undefined ? "undefined" : "null") + " — refusing.");
    return null;
  }
  try {
    var key = await deriveKey(password);
    var enc = new TextEncoder();
    var iv = window.crypto.getRandomValues(new Uint8Array(12));
    var serialized = JSON.stringify(data);
    if (serialized === undefined) {
      // JSON.stringify can return undefined for unsupported values (functions,
      // symbols, undefined). Bail rather than encode the literal string "undefined".
      console.error("JSON.stringify returned undefined — cannot encrypt.");
      return null;
    }
    var encrypted = await window.crypto.subtle.encrypt(
      { name: "AES-GCM", iv: iv },
      key,
      enc.encode(serialized)
    );
    var combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);
    return bytesToBase64(combined);
  } catch (e) {
    console.error("Encryption error:", e);
    return null;
  }
}

export async function decryptData(encryptedStr, password) {
  try {
    var key = await deriveKey(password);
    var raw = Uint8Array.from(atob(encryptedStr), function (c) { return c.charCodeAt(0); });
    var iv = raw.slice(0, 12);
    var data = raw.slice(12);
    var decrypted = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv: iv },
      key,
      data
    );
    return JSON.parse(new TextDecoder().decode(decrypted));
  } catch (e) {
    console.error("Decryption error:", e);
    return null;
  }
}

// ── Current session user ID (set after login) ──
var currentUserId = null;

export function setCurrentUserId(id) {
  currentUserId = id;
}

export function getCurrentUserId() {
  return currentUserId;
}

// ── User Management ──

// Simple hash for PIN storage (not reversible)
async function hashPin(pin) {
  var enc = new TextEncoder();
  var hash = await window.crypto.subtle.digest("SHA-256", enc.encode("vault-pin-" + pin));
  return bytesToBase64(new Uint8Array(hash));
}

// Look up user by username, verify PIN
export async function loginUser(username, pin) {
  try {
    var { data, error } = await supabase
      .from("app_users")
      .select("*")
      .eq("username", username.toLowerCase().trim())
      .single();

    if (error || !data) return { success: false, error: "Invalid username" };

    var pinHash = await hashPin(pin);
    if (pinHash !== data.pin_hash) return { success: false, error: "Invalid PIN" };

    currentUserId = data.id;
    return { success: true, user: data };
  } catch (e) {
    console.error("Login error:", e);
    return { success: false, error: "Login failed" };
  }
}

// Create a new user (admin only)
export async function createUser(username, pin, isAdmin) {
  try {
    var pinHash = await hashPin(pin);
    var { data, error } = await supabase
      .from("app_users")
      .insert({
        username: username.toLowerCase().trim(),
        pin_hash: pinHash,
        is_admin: isAdmin || false,
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) {
      console.error("Create user error:", error);
      if (error.code === "23505") return { success: false, error: "Username already exists" };
      return { success: false, error: "Failed to create user" };
    }

    return { success: true, user: data };
  } catch (e) {
    console.error("Create user error:", e);
    return { success: false, error: "Failed to create user" };
  }
}

// List all users (admin only)
export async function listUsers() {
  try {
    var { data, error } = await supabase
      .from("app_users")
      .select("id, username, is_admin, created_at")
      .order("created_at", { ascending: true });

    if (error) return [];
    return data || [];
  } catch (e) {
    return [];
  }
}

// Delete a user and their data (admin only)
export async function deleteUser(userId) {
  try {
    // Delete their budget data first
    await supabase.from("budget_data").delete().eq("user_id", userId);
    // Delete the user
    var { error } = await supabase.from("app_users").delete().eq("id", userId);
    return !error;
  } catch (e) {
    return false;
  }
}

// Update a user's PIN
export async function updateUserPin(userId, newPin) {
  try {
    var pinHash = await hashPin(newPin);
    var { error } = await supabase
      .from("app_users")
      .update({ pin_hash: pinHash })
      .eq("id", userId);
    return !error;
  } catch (e) {
    return false;
  }
}

// Check if any users exist (for first-time setup)
export async function hasUsers() {
  try {
    var { data, error } = await supabase
      .from("app_users")
      .select("id")
      .limit(1);
    return data && data.length > 0;
  } catch (e) {
    return false;
  }
}

// ── Data Storage (scoped to current user) ──

export async function saveData(dataKey, value) {
  if (!currentUserId) { console.error("No user logged in"); return false; }
  // Refuse to save undefined or null. These would JSON.stringify to the literal
  // values "undefined" and "null" (or empty bytes for undefined), producing
  // database rows that fail to decrypt cleanly. This is a defense-in-depth
  // check — the calling code should never pass these, but if a bug ever does,
  // we'd rather fail loudly here than silently corrupt the row.
  if (value === undefined || value === null) {
    console.error("Refusing to save " + (value === undefined ? "undefined" : "null") + " for key '" + dataKey + "'. This is a bug — please report.");
    return false;
  }
  try {
    var storedValue = await encryptData(value, "vault-budget-encryption-key");
    if (!storedValue) return false;

    var { error } = await supabase
      .from("budget_data")
      .upsert(
        { user_id: currentUserId, data_key: dataKey, encrypted_value: storedValue, updated_at: new Date().toISOString() },
        { onConflict: "user_id,data_key" }
      );

    if (error) { console.error("Save error for " + dataKey + ":", error); return false; }
    return true;
  } catch (e) {
    console.error("Save error:", e);
    return false;
  }
}

// Returns { status: "ok", value } | { status: "missing" } | { status: "error", error }.
// The distinction matters for the caller: a missing row is a normal first-load
// state, but an error (decrypt failure, network blip, etc.) means we must NOT
// overwrite the database on next save — we'd potentially clobber good data.
async function loadDataWithStatus(dataKey) {
  if (!currentUserId) return { status: "error", error: "no user" };
  try {
    var { data, error } = await supabase
      .from("budget_data")
      .select("encrypted_value")
      .eq("user_id", currentUserId)
      .eq("data_key", dataKey)
      .maybeSingle();

    if (error) {
      console.error("Load error for " + dataKey + ":", error);
      return { status: "error", error: error };
    }
    if (!data) {
      // No row — this is a normal "first time this key has been written" state.
      return { status: "missing" };
    }
    var decoded = await decryptData(data.encrypted_value, "vault-budget-encryption-key");
    if (decoded === null) {
      // Row exists but decryption failed — treat as error (don't overwrite).
      return { status: "error", error: "decrypt failed" };
    }
    return { status: "ok", value: decoded };
  } catch (e) {
    console.error("Load error for " + dataKey + ":", e);
    return { status: "error", error: e };
  }
}

// Backwards-compatible: returns the value or null. Used by any external caller
// that doesn't care about the missing/error distinction.
export async function loadData(dataKey) {
  var result = await loadDataWithStatus(dataKey);
  return result.status === "ok" ? result.value : null;
}

// Returns { ok: { key: value }, missing: [keys], errored: [keys] }.
// Callers use the per-key status to decide which keys are safe to save.
export async function loadAllData() {
  var keys = ["bills", "cards", "income", "payroll", "oneTimeTxs", "vaChanges", "salaryChanges", "startingBalance", "savingsBalance", "themeMode", "scenarios", "cardTargetPct"];
  var result = { ok: {}, missing: [], errored: [] };
  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];
    var r = await loadDataWithStatus(key);
    if (r.status === "ok") {
      result.ok[key] = r.value;
    } else if (r.status === "missing") {
      result.missing.push(key);
    } else {
      result.errored.push(key);
    }
  }
  return result;
}

export async function saveAllData(appState) {
  var saves = [];
  if (appState.bills !== undefined) saves.push(saveData("bills", appState.bills));
  if (appState.cards !== undefined) saves.push(saveData("cards", appState.cards));
  if (appState.income !== undefined) saves.push(saveData("income", appState.income));
  if (appState.payroll !== undefined) saves.push(saveData("payroll", appState.payroll));
  if (appState.oneTimeTxs !== undefined) saves.push(saveData("oneTimeTxs", appState.oneTimeTxs));
  if (appState.vaChanges !== undefined) saves.push(saveData("vaChanges", appState.vaChanges));
  if (appState.salaryChanges !== undefined) saves.push(saveData("salaryChanges", appState.salaryChanges));
  if (appState.startingBalance !== undefined) saves.push(saveData("startingBalance", appState.startingBalance));
  if (appState.savingsBalance !== undefined) saves.push(saveData("savingsBalance", appState.savingsBalance));
  if (appState.themeMode !== undefined) saves.push(saveData("themeMode", appState.themeMode));
  if (appState.scenarios !== undefined) saves.push(saveData("scenarios", appState.scenarios));
  if (appState.cardTargetPct !== undefined) saves.push(saveData("cardTargetPct", appState.cardTargetPct));
  await Promise.all(saves);
}